import { useEffect, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { fetchWithProgress, markLoadDone } from "./loadProgress";

function readYaw(fallback: number) {
  try {
    const q = new URLSearchParams(window.location.search).get("panoYaw");
    if (q != null && q !== "") return Number(q);
  } catch { /* noop */ }
  return fallback;
}

export function PanoWorld({
  url,
  radius = 20,
  rotationYDeg = 0,
  flipVertical = false,
}: {
  url: string;
  radius?: number;
  rotationYDeg?: number;
  /** Marble sometimes exports an inverted up-axis — flip the sphere in Y. */
  flipVertical?: boolean;
}) {
  const { scene } = useThree();
  const [failed, setFailed] = useState(false);
  const meshRef = useRef<THREE.Mesh | null>(null);

  // Keep the pano sphere CENTRED ON THE CAMERA every frame. OrbitControls orbits
  // the camera on a tiny sphere around the look target, so a world-fixed pano
  // exhibits parallax against the projected pins (they drift/"float up and down"
  // while looking around). Anchoring the sphere to the camera makes the pano a
  // pure background at infinity: view depends only on camera ORIENTATION, and the
  // pins (also projected relative to the camera) stay perfectly glued.
  useFrame(({ camera }) => {
    if (meshRef.current) meshRef.current.position.copy(camera.position);
  });

  useEffect(() => {
    let disposed = false;
    let mesh: THREE.Mesh | null = null;

    (async () => {
      try {
        let objectUrl: string | null = null;
        try {
          objectUrl = await fetchWithProgress(url);
        } catch {
          objectUrl = null;
        }
        let tex: THREE.Texture;
        try {
          tex = await new THREE.TextureLoader().loadAsync(objectUrl ?? url);
        } finally {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          markLoadDone(url);
        }
        if (disposed) {
          tex.dispose();
          return;
        }
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.needsUpdate = true;
        mesh = new THREE.Mesh(
          new THREE.SphereGeometry(radius, 64, 48),
          new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, depthWrite: false }),
        );
        // Initial position; the useFrame above re-centres it on the camera each
        // frame so look-around never clips into walls and pins never drift.
        mesh.position.set(0, 1.5, 0);
        const yaw = readYaw(rotationYDeg);
        mesh.rotation.y = (yaw * Math.PI) / 180;
        if (flipVertical) {
          // Invert up without breaking BackSide winding for the equirect.
          mesh.scale.y = -1;
        }
        meshRef.current = mesh;
        scene.add(mesh);
        (window as unknown as { __panoStage?: string; __panoYaw?: number; __panoUrl?: string }).__panoStage = "ready";
        (window as unknown as { __panoYaw?: number }).__panoYaw = yaw;
        (window as unknown as { __panoUrl?: string }).__panoUrl = url;

        const onYaw = (ev: Event) => {
          const y = (ev as CustomEvent).detail;
          if (typeof y === "number" && mesh) {
            mesh.rotation.y = (y * Math.PI) / 180;
            (window as unknown as { __panoYaw?: number }).__panoYaw = y;
          }
        };
        window.addEventListener("cf-pano-yaw", onYaw);
        (mesh as unknown as { __onYaw?: (ev: Event) => void }).__onYaw = onYaw;
      } catch (err) {
        console.warn("[sim3d] pano load failed", err);
        (window as unknown as { __panoStage?: string }).__panoStage = "failed";
        if (!disposed) setFailed(true);
      }
    })();

    return () => {
      disposed = true;
      if (mesh) {
        if (meshRef.current === mesh) meshRef.current = null;
        const onYaw = (mesh as unknown as { __onYaw?: (ev: Event) => void }).__onYaw;
        if (onYaw) window.removeEventListener("cf-pano-yaw", onYaw);
        scene.remove(mesh);
        const mat = mesh.material as THREE.MeshBasicMaterial;
        mat.map?.dispose();
        mat.dispose();
        mesh.geometry.dispose();
      }
    };
  }, [url, radius, rotationYDeg, flipVertical, scene]);

  if (failed) return null;
  return null;
}
